'use client';

import { useGetJobsQuery } from '@/features/jobs/api/jobsApi';
import { SmartInput } from '@web/ui';

import type { Job } from '@/features/jobs/types';

type JobSelectFieldProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  name?: string;
  error?: string;
  disabled?: boolean;
  emptyLabel?: string;
  size?: 'small' | 'medium';
};

const normalizeJobs = (raw: unknown): Job[] => {
  if (Array.isArray(raw)) return raw as Job[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).results)) {
    return (raw as any).results as Job[];
  }
  return [];
};

const getJobOptionLabel = (job: Job) => {
  const rawName = (job.name ?? '').trim();
  const name = rawName || 'Untitled job';
  const number =
    job.job_number !== null && job.job_number !== undefined
      ? `#${job.job_number}`
      : null;

  return number ? `${number} - ${name}` : name;
};

export function JobSelectField({
  value,
  onChange,
  label = 'Job',
  name = 'job-select',
  error,
  disabled = false,
  emptyLabel = 'Select job',
  size = 'small',
}: JobSelectFieldProps) {
  const { data, isFetching } = useGetJobsQuery({
    page_size: 100,
  });

  const jobs = normalizeJobs(data?.data);
  const options = [
    { value: '', label: emptyLabel },
    ...jobs.map((job) => ({
      value: job.id,
      label: getJobOptionLabel(job),
    })),
  ];

  return (
    <SmartInput
      type="select"
      name={name}
      label={label}
      size={size}
      value={value}
      options={options}
      readOnly={disabled || isFetching}
      error={error}
      onChange={(nextValue) => onChange(String(nextValue))}
    />
  );
}
